import {
  FunctionComponent,
  useCallback,
  useState,
  ChangeEvent,
  useEffect,
} from 'react'
import { MdCheck } from 'react-icons/md'
import { IoMdEyeOff, IoMdEye } from 'react-icons/io'
import { useHistory } from 'react-router-dom'
import * as Yup from 'yup'

import iconAmpulheta from 'shared/assets/icons/icon-ampulheta.png'
import iconKey from 'shared/assets/icons/icon-key.svg'
import Form, { IProps as FormProps } from 'shared/components/Form'
import Input from 'shared/components/Input'
import ValidatePassword from 'shared/components/ValidatePassword'
import { colors } from 'shared/styles/colors'

import { Feedback } from 'shared/components/Feedback'
import Text from 'shared/components/Text'
import { registerEvent } from 'shared/services/TrackingGAService'
import dimensions from 'shared/styles/dimensions'
import Page from 'shared/components/Page'

import {
  ContainerForm,
  StyledButton,
  Icon,
  ContainerInput,
  ButtonShowPassword,
  HelperMessage,
  ContainerButton,
  ContainerAtencao,
} from './styles'
import { useNewPassword } from 'modules/user/hooks/newPassword'
import { NewPasswordProps } from './NewPasswordComponent.types'

import { steps } from '../..'

const NewPasswordScreen: FunctionComponent<NewPasswordProps> = ({
  createFrotistaImpl,
  setSelectedStep,
}) => {
  const history = useHistory()
  const {
    newUserPassword,
    setNewPassword,
    isPasswordValid,
    confirmPassword,
    setConfirmPassword,
  } = useNewPassword()
  const [signUpOk, setSignUpOk] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false)

  const clearInputsPassword = () => {
    setNewPassword('')
    setConfirmPassword('')
  }

  useEffect(() => clearInputsPassword(), [])

  const clearAllLocalStorage = () => {
    localStorage.removeItem('@TMov:SignUpCnpj')
    localStorage.removeItem('@TMov:SignUpEmail')
    localStorage.removeItem('@TMov:SignUpPhone')
    localStorage.removeItem('@TMov:SignUpPhotoBase64')
    localStorage.removeItem('@TMov:SignUpCep')
    localStorage.removeItem('@TMov:SignUpCidadeIBGE')
    localStorage.removeItem('@TMov:SignUpCidade')
    localStorage.removeItem('@TMov:SignUpEstado')
    localStorage.removeItem('@TMov:SignUpLogradouro')
    localStorage.removeItem('@TMov:SignUpNumero')
    localStorage.removeItem('@TMov:SignUpBairro')
    localStorage.removeItem('@TMov:SignUpComplemento')
    localStorage.removeItem('@TMov:SignUpAntt')
    localStorage.removeItem('@TMov:SignUpPhotoName')
  }

  const createFrotista = useCallback(
    async (values: FormProps['values']) => {
      try {
        await createFrotistaImpl.execute({
          antt: localStorage.getItem('@TMov:SignUpAntt'),
          cnpj: localStorage.getItem('@TMov:SignUpCnpj'),
          email: localStorage.getItem('@TMov:SignUpEmail'),
          telefone: localStorage.getItem('@TMov:SignUpPhone'),
          doc_imagem_frente: localStorage.getItem('@TMov:SignUpPhotoBase64'),
          doc_imagem_verso: localStorage.getItem('@TMov:SignUpPhotoBase64'),
          doc_token_sdk: '',
          endereco: {
            cep: localStorage.getItem('@TMov:SignUpCep'),
            cidade_ibge: localStorage.getItem('@TMov:SignUpCidadeIBGE'),
            cidade: localStorage.getItem('@TMov:SignUpCidade'),
            estado: localStorage.getItem('@TMov:SignUpEstado'),
            numero: localStorage.getItem('@TMov:SignUpNumero'),
            complemento: localStorage.getItem('@TMov:SignUpComplemento'),
            logradouro: localStorage.getItem('@TMov:SignUpLogradouro'),
            bairro: localStorage.getItem('@TMov:SignUpBairro'),
          },
          senha: values.newPassword,
        })

        clearAllLocalStorage()
        setLoading(false)
        setSignUpOk(true)
      } catch (e: unknown) {
        setLoading(false)
        if (e instanceof Error) {
          setErrorMessage(e.message)
        } else {
          setErrorMessage('Erro ao tentar criar frotista')
        }
      }
    },
    [createFrotistaImpl],
  )

  const handleSubmit = useCallback(
    async (values: FormProps['values']) => {
      registerEvent({
        categoria: 'Cadastro',
        acao: 'sign_up',
        local: 'Botao na tela de criação da senha',
      })

      setLoading(true)
      setErrorMessage('')

      try {
        const schema = Yup.object().shape({
          newPassword: Yup.string().nullable(),
          confirmPassword: Yup.string().oneOf(
            [Yup.ref('newPassword'), null],
            'As senhas não são iguais. Tente novamente.',
          ),
        })

        await schema.validate(values, {
          abortEarly: false,
        })

        createFrotista(values)
        return
      } catch (err: unknown) {
        setLoading(false)
        if (err instanceof Yup.ValidationError) {
          setErrorMessage(err.message)
        } else if (err instanceof Error) {
          setErrorMessage(err.message)
        } else {
          setErrorMessage('Erro tentar setar a nova senha')
        }
      }
    },
    [createFrotista],
  )

  if (signUpOk) {
    return (
      <div style={{ paddingTop: 50 }}>
        <Feedback
          icon={iconAmpulheta}
          colorIcon={colors.white}
          title="Solicitação de cadastro efetuada com sucesso!"
          subTitle="Estamos analisando os dados e em breve te retornaremos."
          body={
            <ContainerAtencao>
              <Text
                color={colors.orange}
                size={dimensions.fontSize.largex2}
                weight={'600'}
              >
                ATENÇÃO:
              </Text>
              <Text
                color={colors.white}
                size={dimensions.fontSize.large}
                weight={'normal'}
              >
                Fique atento ao seu email. Caso não tenho recebido a <br />
                confirmação de cadastro, verifique sua caixa de spam.
              </Text>
            </ContainerAtencao>
          }
          onClickButton={() => {
            history.push('/')
          }}
        />
      </div>
    )
  }

  return (
    <>
      <Page
        icon={iconKey}
        title="Criar senha"
        goBackTo={() => {
          setSelectedStep(steps.Antt)
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
          <Form
            onSubmit={handleSubmit}
            initialValues={{
              newPassword: '',
              confirmPassword: '',
            }}
            validationSchema={() => ({
              newPassword: Yup.string().nullable(),
              confirmPassword: Yup.string().nullable(),
            })}
          >
            {formControl => (
              <ContainerForm>
                <ContainerInput>
                  <Input
                    placeholder="Criar senha"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    formControl={formControl}
                    errorMessage={''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setNewPassword(e.target.value)
                    }}
                  >
                    <ButtonShowPassword
                      onClick={() => {
                        setShowNewPassword(!showNewPassword)
                      }}
                    >
                      <Icon>
                        {!showNewPassword ? <IoMdEye /> : <IoMdEyeOff />}
                      </Icon>
                    </ButtonShowPassword>
                  </Input>
                </ContainerInput>
                <ContainerInput>
                  <Input
                    placeholder="Confirme sua senha"
                    name="confirmPassword"
                    type={showConfirmNewPassword ? 'text' : 'password'}
                    formControl={formControl}
                    errorMessage={''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setConfirmPassword(e.target.value)
                    }}
                  >
                    <ButtonShowPassword
                      onClick={() => {
                        setShowConfirmNewPassword(!showConfirmNewPassword)
                      }}
                    >
                      <Icon>
                        {!showConfirmNewPassword ? <IoMdEye /> : <IoMdEyeOff />}
                      </Icon>
                    </ButtonShowPassword>
                  </Input>
                </ContainerInput>

                {errorMessage && <HelperMessage>{errorMessage}</HelperMessage>}

                <ValidatePassword />

                <ContainerButton>
                  <StyledButton
                    color={
                      newUserPassword && confirmPassword && isPasswordValid
                        ? colors.green
                        : colors.grayLight
                    }
                    type="submit"
                    onClick={formControl.handleSubmit}
                    loading={loading}
                    disabled={
                      !(
                        newUserPassword &&
                        confirmPassword &&
                        isPasswordValid
                      ) || loading
                    }
                  >
                    <MdCheck /> Salvar
                  </StyledButton>
                </ContainerButton>
              </ContainerForm>
            )}
          </Form>
        </div>
      </Page>
    </>
  )
}

export default NewPasswordScreen
